/**
 * Phase D+E: Issuer Needs-Split Review UI
 * /admin/merchants/issuer-needs-split
 */

import { useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/contexts/AuthContext";
import {
  useIssuerNeedsSplitList,
  useIssuerNeedsSplitMutations,
  useIssuerMerchantSearch,
  useIssuerApplyPreview,
  type IssuerNeedsSplitRow,
} from "@/hooks/useIssuerNeedsSplit";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  ChevronDown,
  ChevronRight,
  Link2,
  CheckCircle,
  EyeOff,
  Loader2,
  Play,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const STATUS_COLORS: Record<string, string> = {
  new: "bg-blue-100 text-blue-800",
  triaged: "bg-yellow-100 text-yellow-800",
  ignored: "bg-muted text-muted-foreground",
  resolved: "bg-green-100 text-green-800",
  applied: "bg-emerald-100 text-emerald-800",
};

export default function IssuerNeedsSplit() {
  const { isAdmin } = useAuth();
  const { data: rows = [], isLoading } = useIssuerNeedsSplitList();
  const { updateStatus, setParent } = useIssuerNeedsSplitMutations();
  const { preview, apply } = useIssuerApplyPreview();

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [mapModalRow, setMapModalRow] = useState<IssuerNeedsSplitRow | null>(null);
  const [applyModalRow, setApplyModalRow] = useState<IssuerNeedsSplitRow | null>(null);
  const [previewData, setPreviewData] = useState<{
    deals_to_update: number;
    parent_merchant_name: string;
  } | null>(null);

  if (!isAdmin) {
    return (
      <AdminLayout>
        <p className="text-muted-foreground">Admin access required.</p>
      </AdminLayout>
    );
  }

  const handleApplyClick = async (row: IssuerNeedsSplitRow) => {
    setApplyModalRow(row);
    setPreviewData(null);
    try {
      const result = await preview.mutateAsync(row.id);
      setPreviewData({
        deals_to_update: result.deals_to_update,
        parent_merchant_name: result.parent_merchant_name,
      });
    } catch {
      // error handled by mutation
    }
  };

  const handleApplyConfirm = async () => {
    if (!applyModalRow) return;
    await apply.mutateAsync(applyModalRow.id);
    setApplyModalRow(null);
    setPreviewData(null);
  };

  return (
    <AdminLayout>
      <h1 className="text-2xl font-bold mb-2">Issuer Needs-Split Review</h1>
      <p className="text-sm text-muted-foreground mb-4">
        Multi-brand strings detected during normalization. Review and map to parent issuers.
      </p>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground">No needs-split rows found.</p>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Brand (raw)</TableHead>
                <TableHead className="w-24 text-center">Occurrences</TableHead>
                <TableHead className="w-36">Last Seen</TableHead>
                <TableHead className="w-28">Status</TableHead>
                <TableHead>Parent Mapping</TableHead>
                <TableHead className="w-56">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const isExpanded = expandedId === row.id;
                const canApply =
                  row.status === "resolved" && row.parent_merchant_id !== null;
                return (
                  <>
                    <TableRow key={row.id}>
                      <TableCell>
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : row.id)}
                          className="p-1 rounded hover:bg-muted"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      </TableCell>
                      <TableCell className="font-medium max-w-[300px]">
                        <span className="break-words">{row.brand_raw}</span>
                      </TableCell>
                      <TableCell className="text-center">{row.occurrences}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {row.last_seen_at
                          ? formatDistanceToNow(new Date(row.last_seen_at), { addSuffix: true })
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={STATUS_COLORS[row.status] ?? ""}
                          variant="secondary"
                        >
                          {row.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {row.parent_merchant_name ? (
                          <span className="font-medium text-primary">
                            {row.parent_merchant_name}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {row.status !== "triaged" && row.status !== "applied" && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={updateStatus.isPending}
                              onClick={() =>
                                updateStatus.mutate({ id: row.id, status: "triaged" })
                              }
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Triaged
                            </Button>
                          )}
                          {row.status !== "ignored" && row.status !== "applied" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={updateStatus.isPending}
                              onClick={() =>
                                updateStatus.mutate({ id: row.id, status: "ignored" })
                              }
                            >
                              <EyeOff className="h-3 w-3 mr-1" />
                              Ignore
                            </Button>
                          )}
                          {row.status !== "applied" && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setMapModalRow(row)}
                            >
                              <Link2 className="h-3 w-3 mr-1" />
                              Map Parent
                            </Button>
                          )}
                          {canApply && (
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => handleApplyClick(row)}
                            >
                              <Play className="h-3 w-3 mr-1" />
                              Apply
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Expanded samples row */}
                    {isExpanded && (
                      <TableRow key={`${row.id}-expand`}>
                        <TableCell />
                        <TableCell colSpan={6}>
                          <div className="text-sm space-y-1 py-1">
                            <p className="text-muted-foreground font-medium">Samples</p>
                            {(row.sample_headlines ?? []).length > 0 ? (
                              <ul className="list-disc pl-5 space-y-0.5">
                                {(row.sample_headlines ?? []).map((h, i) => (
                                  <li key={i}>
                                    <span>{h}</span>
                                    {row.sample_promo_merchant_keys?.[i] && (
                                      <span className="text-muted-foreground ml-2">
                                        ({row.sample_promo_merchant_keys[i]})
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-muted-foreground italic">No samples recorded</p>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Parent mapping modal */}
      {mapModalRow && (
        <ParentMapModal
          row={mapModalRow}
          onClose={() => setMapModalRow(null)}
          onSelect={(merchantId) => {
            setParent.mutate(
              { id: mapModalRow.id, parent_merchant_id: merchantId },
              { onSuccess: () => setMapModalRow(null) }
            );
          }}
          isSaving={setParent.isPending}
        />
      )}

      {/* Apply confirm modal */}
      {applyModalRow && (
        <Dialog open onOpenChange={(open) => !open && setApplyModalRow(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Apply Parent Mapping</DialogTitle>
              <DialogDescription>
                Brand: <strong>{applyModalRow.brand_raw}</strong>
              </DialogDescription>
            </DialogHeader>

            {preview.isPending ? (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Checking affected deals…
              </div>
            ) : previewData ? (
              <div className="space-y-2 text-sm">
                <p>
                  Parent: <strong>{previewData.parent_merchant_name}</strong>
                </p>
                <p>
                  Deals to update:{" "}
                  <strong>{previewData.deals_to_update}</strong>
                </p>
                {previewData.deals_to_update === 0 && (
                  <p className="text-muted-foreground italic">
                    No matching deals with null purchase_issuer_uuid found.
                  </p>
                )}
                <p className="text-muted-foreground text-xs">
                  This will set <code>purchase_issuer_uuid</code> on matching
                  production deals. Only deals with a null UUID will be updated.
                </p>
              </div>
            ) : (
              <p className="text-sm text-destructive">
                Failed to load preview. Close and retry.
              </p>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setApplyModalRow(null);
                  setPreviewData(null);
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={
                  !previewData ||
                  previewData.deals_to_update === 0 ||
                  apply.isPending
                }
                onClick={handleApplyConfirm}
              >
                {apply.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    Applying…
                  </>
                ) : (
                  <>
                    <Play className="h-3 w-3 mr-1" />
                    Confirm Apply
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </AdminLayout>
  );
}

function ParentMapModal({
  row,
  onClose,
  onSelect,
  isSaving,
}: {
  row: IssuerNeedsSplitRow;
  onClose: () => void;
  onSelect: (merchantId: string) => void;
  isSaving: boolean;
}) {
  const [search, setSearch] = useState("");
  const { data: merchants = [], isLoading } = useIssuerMerchantSearch(search);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Map Parent Issuer</DialogTitle>
          <DialogDescription>
            Brand: <strong>{row.brand_raw}</strong>
          </DialogDescription>
        </DialogHeader>

        <Input
          placeholder="Search issuers…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />

        <div className="max-h-64 overflow-y-auto border rounded-md">
          {isLoading ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : merchants.length === 0 ? (
            <p className="text-sm text-muted-foreground p-4 text-center">
              No issuer merchants found
            </p>
          ) : (
            merchants.map((m) => (
              <button
                key={m.id}
                disabled={isSaving}
                onClick={() => onSelect(m.id)}
                className="w-full text-left px-3 py-2 hover:bg-muted border-b last:border-b-0 text-sm flex justify-between items-center"
              >
                <div>
                  <span className="font-medium">{m.display_name}</span>
                  <span className="text-muted-foreground ml-2 text-xs">
                    {m.merchant_key}
                  </span>
                </div>
                <Badge variant="outline" className="text-xs">
                  {m.merchant_type}
                </Badge>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
